// Montører: små grønne varevogne og montørskibet. De kører langs en rute til en ledning og
// reparerer, forstærker eller (skibet) holder vagt mod en ankerbåd. Ingen teleport: alt tager tid.

import { difficulty } from './difficulty';
import { markDirty } from './flow';
import { dist, lineById, lineMid, nodeById, repairTicks, SHIP_KM_PER_TICK, updateShields, VAN_KM_PER_TICK } from './grid';
import type { Crew, CrewKind, GridNode, Line, Pt, SimEvent, State } from './types';

/** Forstærkning af en mast tager 1 s, når holdet er fremme. */
export const REINFORCE_TICKS = 10;
export const MAX_CREWS = 6;
/** Montørskibet holder vagt så længe ved kablet, når det er fremme. */
export const GUARD_TICKS = 60;
/** Vejen langs nettet må højst være så meget længere end fugleflugt (promille, plus 10 km). */
const DETOUR_PM = 1600;

export function crewKindFor(line: Line): CrewKind {
  return line.medium === 'sea' || line.medium === 'hvdc' ? 'ship' : 'van';
}

export function addCrew(state: State, kind: CrewKind, homeId: string, events: SimEvent[]): Crew {
  const home = nodeById(state, homeId);
  const crew: Crew = {
    id: `${kind === 'ship' ? 'S' : 'C'}${state.nextCrewId++}`,
    kind,
    x: home?.x ?? 0,
    y: home?.y ?? 0,
    task: 'idle',
    lineId: '',
    ticks: 0,
    then: 'repair',
    route: [],
  };
  state.crews.push(crew);
  events.push({ t: 'crew_hired', tick: state.tick, crew: crew.id });
  return crew;
}

/** Er der allerede et hold på vej til eller i gang med denne ledning? */
export function crewOnLine(state: State, lineId: string): Crew | undefined {
  for (const c of state.crews) if (c.task !== 'idle' && c.lineId === lineId) return c;
  return undefined;
}

/** Nærmeste ledige hold af den rigtige slags (fast rækkefølge ved lige afstand). */
export function nearestIdle(state: State, line: Line): Crew | undefined {
  const kind = crewKindFor(line);
  const mid = lineMid(state, line);
  let best: Crew | undefined;
  let bestD = Infinity;
  for (const c of state.crews) {
    if (c.kind !== kind || c.task !== 'idle') continue;
    const d = dist(c, mid);
    if (d < bestD) {
      bestD = d;
      best = c;
    }
  }
  return best;
}

function routeLength(from: Pt, route: readonly Pt[]): number {
  let len = 0;
  let p = from;
  for (const q of route) {
    len += dist(p, q);
    p = q;
  }
  return len;
}

function landNode(n: GridNode): boolean {
  return n.active && n.kind !== 'offshore' && n.kind !== 'foreign';
}

/**
 * Vejen for en varevogn: til nærmeste knude, langs nettets landledninger (vejene følger masterne)
 * til den nærmeste ende af ledningen, og ud til midten. Er der ingen vej, eller er omvejen for lang,
 * køres der direkte. Skibet sejler direkte. Dijkstra med fast rækkefølge, så det er deterministisk.
 */
export function planRoute(state: State, crew: Crew, line: Line): Pt[] {
  const mid = lineMid(state, line);
  if (crew.kind === 'ship') return [mid];
  let start: GridNode | undefined;
  let sd = Infinity;
  for (const n of state.nodes) {
    if (!landNode(n)) continue;
    const d = dist(crew, n);
    if (d < sd) {
      sd = d;
      start = n;
    }
  }
  if (!start) return [mid];
  const D = new Map<string, number>([[start.id, 0]]);
  const prev = new Map<string, string>();
  const done = new Set<string>();
  for (;;) {
    let u: string | null = null;
    let best = Infinity;
    for (const [id, d] of D) {
      if (done.has(id)) continue;
      if (d < best || (d === best && u !== null && id < u)) {
        best = d;
        u = id;
      }
    }
    if (u === null) break;
    done.add(u);
    if (u === line.a || u === line.b) break;
    const un = nodeById(state, u) as GridNode;
    for (const l of state.lines) {
      if (l.medium !== 'air' && l.medium !== 'cable') continue;
      const v = l.a === u ? l.b : l.b === u ? l.a : null;
      if (v === null || done.has(v)) continue;
      const vn = nodeById(state, v);
      if (!vn || !landNode(vn)) continue;
      const nd = best + dist(un, vn);
      if (nd < (D.get(v) ?? Infinity)) {
        D.set(v, nd);
        prev.set(v, u);
      }
    }
  }
  const end = done.has(line.a) ? line.a : done.has(line.b) ? line.b : null;
  if (end === null) return [mid];
  const path: Pt[] = [];
  for (let id: string | undefined = end; id !== undefined; id = prev.get(id)) {
    const n = nodeById(state, id) as GridNode;
    path.unshift({ x: n.x, y: n.y });
  }
  path.push({ x: mid.x, y: mid.y });
  if (path.length > 1 && dist(crew, path[0] as Pt) < 0.5) path.shift();
  const direct = dist(crew, mid);
  if (routeLength(crew, path) * 1000 > direct * DETOUR_PM + 10_000) return [mid];
  return path;
}

function speedOf(crew: Crew): number {
  return crew.kind === 'ship' ? SHIP_KM_PER_TICK : VAN_KM_PER_TICK;
}

/** Ticks for at køre ruten. */
export function routeTicks(crew: Crew, route: readonly Pt[]): number {
  return Math.max(1, Math.ceil(routeLength(crew, route) / speedOf(crew)));
}

/** Hvor længe holdet er om at nå frem til ledningen (til coach og UI). */
export function travelTicks(state: State, crew: Crew, line: Line): number {
  return routeTicks(crew, planRoute(state, crew, line));
}

export function dispatch(state: State, crew: Crew, line: Line, then: Crew['then'], events: SimEvent[]): void {
  crew.task = 'driving';
  crew.lineId = line.id;
  crew.then = then;
  crew.route = planRoute(state, crew, line);
  crew.ticks = routeTicks(crew, crew.route);
  events.push({ t: 'crew_dispatched', tick: state.tick, crew: crew.id, line: line.id, etaTicks: crew.ticks });
}

/**
 * Montørerne kører selv: hvert knækket stykke uden hold får det nærmeste ledige hold af den rigtige
 * slags. Ledningerne gennemgås i fast rækkefølge, så det er deterministisk. Spilleren skal kun
 * sørge for, at der er hold nok. Et skib på vagt ved et kabel, der alligevel knækker, går i gang.
 */
export function autoDispatch(state: State, events: SimEvent[]): void {
  for (const line of state.lines) {
    if (line.status !== 'broken' && !line.halfDown) continue;
    const on = crewOnLine(state, line.id);
    if (on) {
      if (on.then === 'guard') on.then = 'repair';
      if (on.task === 'guarding') {
        on.task = 'repairing';
        on.ticks = repairTicks(line);
      }
      continue;
    }
    const crew = nearestIdle(state, line);
    if (crew) dispatch(state, crew, line, 'repair', events);
  }
}

/** Kør et stykke langs ruten. */
function drive(crew: Crew): void {
  let left = speedOf(crew);
  while (left > 0 && crew.route.length > 0) {
    const q = crew.route[0] as Pt;
    const d = dist(crew, q);
    if (d <= left) {
      crew.x = q.x;
      crew.y = q.y;
      crew.route.shift();
      left -= d;
    } else {
      crew.x += ((q.x - crew.x) * left) / d;
      crew.y += ((q.y - crew.y) * left) / d;
      left = 0;
    }
  }
}

function goIdle(crew: Crew): void {
  crew.task = 'idle';
  crew.lineId = '';
  crew.route = [];
}

/** Ét tick for alle hold. */
export function updateCrews(state: State, events: SimEvent[]): void {
  for (const crew of state.crews) {
    if (crew.task === 'idle') continue;
    const line = lineById(state, crew.lineId);
    if (!line) {
      goIdle(crew);
      continue;
    }
    if (crew.task === 'driving') {
      drive(crew);
      crew.ticks--;
      if (crew.ticks > 0) continue;
      const mid = lineMid(state, line);
      crew.x = mid.x;
      crew.y = mid.y;
      crew.route = [];
      if (crew.then === 'repair') {
        if (line.status !== 'broken' && !line.halfDown) {
          goIdle(crew);
          continue;
        }
        crew.task = 'repairing';
        crew.ticks = repairTicks(line);
      } else if (crew.then === 'guard') {
        crew.task = 'guarding';
        crew.ticks = GUARD_TICKS;
      } else {
        if (line.reinforced || line.medium !== 'air') {
          goIdle(crew);
          continue;
        }
        crew.task = 'reinforcing';
        crew.ticks = REINFORCE_TICKS;
      }
      continue;
    }
    crew.ticks--;
    if (crew.ticks > 0) continue;
    if (crew.task === 'repairing') {
      // Et rigtigt brud koster reparation på de milde niveauer; en halv ledning (småt) gør ikke.
      // Kassen går aldrig under nul: montøren lapper altid, også når der ikke er råd.
      const bill = line.status === 'broken' ? Math.min(Math.max(0, Math.floor(state.budget)), difficulty(state.difficulty).repairKr ?? 0) : 0;
      if (bill > 0) {
        state.budget -= bill;
        state.yearStats.repairKr = (state.yearStats.repairKr ?? 0) + bill;
      }
      line.status = 'ok';
      line.halfDown = false;
      line.overTicks = 0;
      state.stats.breaksRepaired++;
      markDirty(state);
      updateShields(state);
      events.push(bill > 0 ? { t: 'line_repaired', tick: state.tick, line: line.id, cost: bill } : { t: 'line_repaired', tick: state.tick, line: line.id });
    } else if (crew.task === 'reinforcing') {
      line.reinforced = true;
      events.push({ t: 'mast_reinforced', tick: state.tick, line: line.id });
    }
    goIdle(crew);
  }
}
